import { useEffect, useRef, useState } from 'react';
import { Newspaper } from 'lucide-react';
import { useActiveConversation } from '../../../chat/ActiveConversationContext';
import { useTranslation } from '../../../../lib/i18n';
import {
  blogDraftFileName,
  blogDraftReadOnlyHtml,
  blogDraftReviewHtml,
  blogDraftTitle,
  blogDraftViewerHtml,
  blogSitePreviewAddress,
  type BlogDraftPreviewCardData,
} from '../../../../lib/blog-draft-preview';
import { fetchBlogDraftPreview } from '../../../../lib/blog-draft-preview-api';
import { useBlogDraftPreviewEnabled } from '../../../../lib/blog-draft-preview-flag';
import { openBlogReview, type SendBlogComments } from '../../../../lib/blog-review-store';
import { useGenericBi } from '../../../../lib/use-generic-bi';
import { useItemSeenBi } from '../../../../lib/use-item-seen-bi';
import { useLoadBi } from '../../../../lib/use-load-bi';
import { openDocViewer } from '../../../../lib/doc-viewer-presenter';
import { reviewNonce, reviewedViewDocument, workspaceForSite } from '../../../../lib/email-review';
import type { SentReviewComment } from '../../../../lib/site-review';
import { useChatOverlayOpen } from '../../../../lib/use-chat-overlay';
import { useIsPhone } from '../../../../lib/use-is-phone';
import { useSitePaneFits } from '../../../../lib/use-site-pane';
import { useWorkspaces } from '../../../../lib/workspaces';
import { Button, Card, Typography } from '../../ui';
import { BlogDraftInlinePreview } from './BlogDraftInlinePreview';
import { useReviewedViewLabels } from './EmailReviewMessage';
import styles from './BlogDraftPreviewCard.module.css';

interface BlogDraftPreviewCardProps {
  card: BlogDraftPreviewCardData;
  conversationId?: string;
  cardKey?: string;
  latest?: boolean;
  canSend?: boolean;
  sendComments?: SendBlogComments;
  pastComments?: readonly SentReviewComment[];
}

export function BlogDraftPreviewCard(props: BlogDraftPreviewCardProps) {
  const enabled = useBlogDraftPreviewEnabled();
  if (enabled !== true) return null;
  return <BlogDraftPreviewCardBody {...props} />;
}

function BlogDraftPreviewCardBody({
  card,
  conversationId,
  cardKey,
  latest = true,
  canSend = true,
  sendComments,
  pastComments,
}: BlogDraftPreviewCardProps) {
  const { t } = useTranslation();
  const reviewedLabels = useReviewedViewLabels(t('blogDraft.sent.title'));
  const { logPageClickBi } = useGenericBi();
  const workspaces = useWorkspaces();
  const workspaceId = workspaces.status === 'ready' ? workspaceForSite(workspaces.workspaces, card.metaSiteId) : null;
  const paneFits = useSitePaneFits();
  const phone = useIsPhone();
  const chatOpen = useChatOverlayOpen();
  const { activeConversationId } = useActiveConversation();
  const beside = paneFits && chatOpen && conversationId != null && conversationId === activeConversationId;
  const [state, setState] = useState<'idle' | 'loading' | 'unavailable'>('idle');
  useLoadBi('blog-draft-preview', { isLoading: state === 'loading', isError: state === 'unavailable' });
  const seenRef = useItemSeenBi('blog_draft_preview_card', { additionalFields: { reviewable: String(Boolean(sendComments)) } });
  const mounted = useRef(true);
  const visible = useRef(false);
  visible.current = chatOpen && (conversationId == null || conversationId === activeConversationId);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const review = () => {
    if (!workspaceId || !canSend || !sendComments) return;
    logPageClickBi({ page_name: 'chat', button_name: 'blog_draft_preview_review' });
    openBlogReview({ card, workspaceId, conversationId: conversationId ?? null, sendComments, beside, revision: cardKey ?? conversationId ?? '' });
  };

  const view = async () => {
    if (!workspaceId) return;
    logPageClickBi({ page_name: 'chat', button_name: 'blog_draft_preview_open' });
    setState('loading');
    try {
      const commented = pastComments?.length ? pastComments : null;
      const preview = commented
        ? await fetchBlogDraftPreview(workspaceId, card.metaSiteId, card.draftPostId, phone ? 'mobile' : 'desktop')
        : await fetchBlogDraftPreview(workspaceId, card.metaSiteId, card.draftPostId);
      if (!mounted.current || !visible.current) {
        if (mounted.current) setState('idle');
        return;
      }
      const title = blogDraftTitle(preview, card.title);
      const sitePreviewUrl = commented
        ? null
        : blogSitePreviewAddress(preview.sitePreviewUrl ?? '', phone ? 'phone' : 'desktop', window.location.origin);
      openDocViewer({
        file: {
          name: blogDraftFileName(title),
          isApp: false,
          content: commented
            ? reviewedViewDocument(blogDraftReviewHtml(preview, title), commented, reviewedLabels(commented), reviewNonce())
            : blogDraftViewerHtml(preview, title),
        },
        kind: 'html',
        pageName: 'document_viewer',
        besideChat: true,
        title: title || undefined,
        localOnly: true,
        ...(sitePreviewUrl ? { sitePreviewUrl } : {}),
        ...(commented ? {} : { viewText: blogDraftReadOnlyHtml(preview, title) }),
      });
      if (mounted.current) setState('idle');
    } catch {
      if (mounted.current) setState('unavailable');
    }
  };

  return (
    <Card ref={seenRef} variant="sunken" size="sm" className={styles.card} data-testid="chat-blog-draft-preview-card">
      <div className={styles.row}>
        <span className={styles.glyph} aria-hidden>
          <Newspaper size={18} />
        </span>
        <span className={styles.text}>
          <Typography variant="p3" color="secondary">
            {t('blogDraft.card.title')}
          </Typography>
          {card.title && (
            <Typography variant="p2" color="primary" className={styles.title}>
              {card.title}
            </Typography>
          )}
          {state === 'unavailable' && (
            <Typography variant="p3" color="secondary" role="alert">
              {t('blogDraft.unavailable')}
            </Typography>
          )}
        </span>
      </div>
      {workspaceId && latest && <BlogDraftInlinePreview workspaceId={workspaceId} card={card} cardKey={cardKey ?? conversationId ?? ''} />}
      {sendComments ? (
        <Button size="small" variant="outlined" disabled={!workspaceId || !canSend} onClick={review} data-testid="blogDraftPreviewReview">
          {t('blogDraft.card.open')}
        </Button>
      ) : (
        <Button
          size="small"
          variant="outlined"
          disabled={!workspaceId}
          loading={state === 'loading'}
          onClick={() => void view()}
          data-testid="blogDraftPreviewOpen"
        >
          {state === 'loading' ? t('blogDraft.loading') : t('blogDraft.card.view')}
        </Button>
      )}
    </Card>
  );
}
